import { memo, useMemo } from 'react';
import { ReactFlowProvider } from '@xyflow/react';
import { ProcessFlowVisualization, cn, type AssigneeLookup } from '@bliks-io/ui';
import type { StepTemplate } from '@bliks-io/contracts';
import { stepTemplateToFlowProcess } from '../adapters/stepTemplateToFlowProcess';

interface Props {
  template: StepTemplate;
  assigneeLookup?: AssigneeLookup;
  height?: number;
  className?: string;
}

/**
 * ponytail: one live canvas per list row. Fine for the tens of templates a
 * tenant accumulates; lazy-mount on viewport intersection if that ever grows
 * into the hundreds.
 */
export const StepTemplateThumbnail = memo(function StepTemplateThumbnail({
  template,
  assigneeLookup,
  height = 112,
  className
}: Props) {
  const { process, events, positions, nodeIds } = useMemo(
    () => stepTemplateToFlowProcess(template),
    [template]
  );

  // Own provider: the editor page wraps everything in a ReactFlowProvider, and
  // a nested <ReactFlow> without its own would share (and clobber) that store.
  // `inert`, not `pointer-events-none`: React Flow's stylesheet re-enables
  // pointer events on every node, so a CSS wrapper rule never reaches them.
  return (
    <div
      inert
      className={cn('overflow-hidden rounded-md border bg-muted/30', className)}
      style={{ height }}
    >
      <ReactFlowProvider>
        <ProcessFlowVisualization
          process={process}
          events={events}
          savedPositions={positions}
          assigneeLookup={assigneeLookup}
          height={height}
          showMinimap={false}
          showControls={false}
          showStepDetail={false}
          scrollBehavior="page"
          showUnattachedIntermediateEvents
          fitViewNodeIds={nodeIds}
        />
      </ReactFlowProvider>
    </div>
  );
});
